import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Search, ChevronLeft, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import { extractError, useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

const EMPTY = { name: "", phone: "", email: "", client_type: "Individual", address: "", city: "", state: "", pincode: "", notes: "", tags: [] };

export default function Clients() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [page, setPage] = useState(1);
  const [perPage] = useState(15);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);

  const canEdit = user.role === "admin" || user.role === "manager";

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 350);
    return () => window.clearTimeout(timer);
  }, [search]);

  const load = async () => {
    const params = { page, per_page: perPage };
    if (debouncedSearch.trim()) params.search = debouncedSearch.trim();
    const { data } = await api.get("/clients", { params });
    setItems(Array.isArray(data) ? data : data.items || []);
    setTotal(Array.isArray(data) ? data.length : data.total || 0);
    setPages(Array.isArray(data) ? 1 : data.pages || 1);
  };
  useEffect(() => { load(); }, [page, debouncedSearch]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.patch(`/clients/${editing}`, form);
        toast.success("Client updated");
      } else {
        await api.post("/clients", form);
        toast.success("Client added");
      }
      setOpen(false);
      setForm(EMPTY);
      setEditing(null);
      load();
    } catch (err) {
      toast.error(extractError(err));
    }
  };

  const openEdit = (c) => {
    setForm({ name: c.name, phone: c.phone, email: c.email || "", client_type: c.client_type || "Individual",
      address: c.address || "", city: c.city || "", state: c.state || "", pincode: c.pincode || "",
      notes: c.notes || "", tags: c.tags || [] });
    setEditing(c.id);
    setOpen(true);
  };

  return (
    <div className="space-y-6" data-testid="clients-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-zinc-500">People</div>
          <h1 className="font-serif text-4xl tracking-tighter mt-1 text-[#1A1A1A]">Clients</h1>
        </div>
        {canEdit && (
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setForm(EMPTY); setEditing(null); } }}>
            <DialogTrigger asChild>
              <Button className="bg-[#1A1A1A] text-white hover:bg-black rounded-full" data-testid="clients-add-btn">
                <Plus size={16} className="mr-1" /> Add clientss
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader><DialogTitle>{editing ? "Edit client" : "New client"}</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="space-y-3" data-testid="client-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div><Label>Name *</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="client-name-input" /></div>
                  <div><Label>Phone *</Label><Input required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="client-phone-input" /></div>
                  <div><Label>Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="client-email-input" /></div>
                  <div><Label>City</Label><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></div>
                  <div className="md:col-span-2"><Label>Address</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
                  <div className="md:col-span-2"><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
                </div>
                <DialogFooter>
                  <Button type="button" variant="ghost" onClick={() => { setOpen(false); setForm(EMPTY); setEditing(null); }}>Cancel</Button>
                  <Button type="submit" className="bg-[#D85C38] hover:bg-[#B84A2A] text-white" data-testid="client-save-btn">{editing ? "Save changes" : "Add client"}</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="relative w-full sm:w-96">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
        <Input placeholder="Search clients…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 h-10 bg-white" data-testid="clients-search-input" />
      </div>

      <div className="hidden md:block bg-white border border-black/10 rounded-md overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Phone</TableHead>
              <TableHead>Email</TableHead>
              <TableHead className="text-right">Bookings</TableHead>
              <TableHead>Last booking</TableHead>
              <TableHead className="text-right">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center text-sm text-zinc-500 py-10">No clients yet.</TableCell></TableRow>
            ) : (
              items.map((c) => (
                <TableRow key={c.id} data-testid={`client-row-${c.id}`}>
                  <TableCell className="font-medium">{c.name}</TableCell>
                  <TableCell className="text-zinc-600">{c.phone}</TableCell>
                  <TableCell className="text-zinc-600">{c.email || "—"}</TableCell>
                  <TableCell className="text-right">{c.bookings_count}</TableCell>
                  <TableCell className="text-zinc-600">{c.last_booking_date || "—"}</TableCell>
                  <TableCell className="text-right">
                    {canEdit && <Button variant="ghost" size="sm" onClick={() => openEdit(c)} data-testid={`client-edit-${c.id}`}>Edit</Button>}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <div className="flex justify-between items-center">
        <div className="text-sm text-zinc-500">Showing {items.length ? (page - 1) * perPage + 1 : 0} - {(page - 1) * perPage + items.length} of {total}</div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>
            <ChevronLeft size={16} />
          </Button>
          <span className="text-sm font-medium">Page {page} of {pages}</span>
          <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>
            <ChevronRight size={16} />
          </Button>
        </div>
      </div>

      <div className="md:hidden space-y-3">
        {items.length === 0 ? (
          <div className="bg-white border border-black/10 rounded-md p-6 text-center text-sm text-zinc-500">No clients yet.</div>
        ) : (
          items.map((c) => (
            <div key={c.id} className="bg-white border border-black/10 rounded-md p-4 shadow-sm">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="font-medium text-sm">{c.name}</div>
                  <div className="text-xs text-zinc-500 mt-1">{c.phone}</div>
                  {c.email && <div className="text-xs text-zinc-500 mt-0.5">{c.email}</div>}
                </div>
                {canEdit && <Button variant="ghost" size="sm" onClick={() => openEdit(c)} data-testid={`client-edit-${c.id}`}>Edit</Button>}
              </div>
              <div className="mt-3 flex items-center justify-between text-xs text-zinc-500">
                <span>{c.bookings_count} bookings</span>
                <span>{c.last_booking_date || "No bookings"}</span>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
